import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Minus, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { redirectToStripe } from "@/lib/redirect";

/**
 * Vinyl fulfillment page — `/vinyl/fulfill/:token`
 *
 * Flow:
 *   1. Buyer lands via personalised email link
 *   2. Adjusts qty (1–5) — 20% off auto-applies at qty ≥ 2
 *   3. Enters shipping address (country drives the shipping cost line)
 *   4. Stripe Checkout opens pre-filled with the address, single shipping
 *      option matching the destination, and the discounted line items.
 */
export default function VinylFulfill() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [qty, setQty] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Shipping address state
  const [shipName, setShipName] = useState("");
  const [country, setCountry] = useState("");
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [city, setCity] = useState("");
  const [stateRegion, setStateRegion] = useState("");
  const [postal, setPostal] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: r } = await api.get(`/vinyl/fulfill/${token}`);
        if (!alive) return;
        setData(r);
        setQty(r.default_quantity || 1);
        setShipName(r.name || "");
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.detail || "We couldn't find this reservation.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const selectedCountry = useMemo(() => {
    if (!data?.countries || !country) return null;
    return data.countries.find((c) => c.code === country) || null;
  }, [data, country]);

  const pricing = useMemo(() => {
    if (!data) return null;
    const q = Math.max(1, Math.min(qty, data.max_quantity || 5));
    const unit = Number(data.unit_price ?? 60);
    const base = +(unit * q).toFixed(2);
    const discountPct = data.is_comp ? 0 : (q >= (data.discount_min_qty || 2) ? (data.discount_pct || 0) : 0);
    const discount = +(base * discountPct).toFixed(2);
    const subtotal = +(base - discount).toFixed(2);
    const shippingAmount = data.is_comp ? 0 : (selectedCountry ? +selectedCountry.shipping_amount : 0);
    const total = +(subtotal + shippingAmount).toFixed(2);
    return { quantity: q, base, discountPct, discount, subtotal, shippingAmount, total };
  }, [data, qty, selectedCountry]);

  const addressComplete = country && shipName.trim() && line1.trim() && city.trim() && postal.trim();

  const onCheckout = async () => {
    if (!data || submitting || !pricing || !addressComplete) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data: r } = await api.post("/checkout/vinyl-fulfill", {
        token,
        quantity: pricing.quantity,
        origin_url: window.location.origin,
        shipping: {
          name: shipName.trim(),
          line1: line1.trim(),
          line2: line2.trim() || null,
          city: city.trim(),
          state: stateRegion.trim() || null,
          postal_code: postal.trim(),
          country,
          phone: phone.trim() || null,
        },
      });
      if (r?.url) {
        redirectToStripe(r.url);
      } else {
        setError("Checkout could not start. Please try again.");
        setSubmitting(false);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      let msg;
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors come back as a list of {loc, msg, type}
        msg = detail.map((d) => `${(d.loc || []).slice(-1)[0] || "field"}: ${d.msg}`).join(" · ");
      } else if (status) {
        msg = `Checkout failed (HTTP ${status}). Please try again in a moment.`;
      } else {
        msg = "Network hiccup — please try again.";
      }
      setError(msg);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main
        className="min-h-screen flex items-center justify-center bg-[var(--mm-bg,#050a1a)] text-white px-6"
        data-testid="vinyl-fulfill-loading"
      >
        <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-white/55">
          Loading your reservation…
        </p>
      </main>
    );
  }

  if (error && !data) {
    return (
      <main
        className="min-h-screen flex flex-col items-center justify-center bg-[var(--mm-bg,#050a1a)] text-white px-6 py-12"
        data-testid="vinyl-fulfill-error"
      >
        <div className="w-full max-w-md text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/55 mb-3">
            Metamorph · Vinyl LP
          </p>
          <h1
            className="text-3xl mb-6"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", letterSpacing: "0.02em", fontWeight: 500 }}
          >
            Link not found
          </h1>
          <p className="text-white/75 text-sm leading-relaxed mb-8">{error}</p>
          <Link
            to="/contact"
            className="font-mono text-[11px] uppercase tracking-[0.28em] text-white/85 underline underline-offset-4 hover:text-[var(--mm-accent)]"
            data-testid="vinyl-fulfill-contact-link"
          >
            Get in touch
          </Link>
        </div>
      </main>
    );
  }

  if (data?.fulfilled) {
    return (
      <main
        className="min-h-screen flex flex-col items-center justify-center bg-[var(--mm-bg,#050a1a)] text-white px-6 py-12"
        data-testid="vinyl-fulfill-already-paid"
      >
        <div className="w-full max-w-md text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/55 mb-3">
            Metamorph · Vinyl LP
          </p>
          <h1
            className="text-3xl mb-4"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", letterSpacing: "0.02em", fontWeight: 500 }}
          >
            Already confirmed
          </h1>
          <p className="text-white/75 text-sm leading-relaxed mb-2">
            This reservation has already been paid in full.
          </p>
          {data.fulfilled_quantity ? (
            <p className="text-white/55 text-sm">
              {data.fulfilled_quantity} record{data.fulfilled_quantity !== 1 ? "s" : ""} confirmed.
            </p>
          ) : null}
        </div>
      </main>
    );
  }

  const inputCls = "bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-3 py-2.5 text-[13px] text-white placeholder-white/35";

  return (
    <main
      className="min-h-screen flex flex-col items-center bg-[var(--mm-bg,#050a1a)] text-white px-6 py-12"
      data-testid="vinyl-fulfill"
    >
      <div className="w-full max-w-md">
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/55 text-center mb-3">
          by George Maple
        </p>
        <h1
          className="deco-text text-4xl sm:text-5xl uppercase text-center leading-[0.95] mb-3"
          style={{ letterSpacing: "0.1em" }}
        >
          Metamorph
        </h1>
        <h2
          className="text-xl sm:text-2xl text-center mb-2"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3f2ee", letterSpacing: "0.02em", fontWeight: 500 }}
        >
          Vinyl LP — confirm your order
        </h2>
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/55 text-center mb-10">
          {data?.name ? `Held for ${data.name}` : `Reserved for ${data?.email || "you"}`}
        </p>

        <div className="border border-white/15 px-5 py-5 mb-5" data-testid="vinyl-fulfill-reservation-card">
          <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-2">
            {data?.is_comp ? "Your gift" : "Your reservation"}
          </p>
          <p className="text-white text-lg leading-snug" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: "0.01em", fontWeight: 500 }}>
            {data?.is_comp ? (
              <>
                {data?.reserved_quantity || 1} record{(data?.reserved_quantity || 1) !== 1 ? "s" : ""} — <span style={{ color: "#f3e0a6" }}>on the house</span>
              </>
            ) : (
              <>
                {data?.reserved_quantity || 1} record{(data?.reserved_quantity || 1) !== 1 ? "s" : ""} at ${Number(data?.unit_price || 60).toFixed(0)} AUD each
              </>
            )}
          </p>
        </div>

        {data?.is_comp ? (
          <div
            className="border px-4 py-3 mb-7"
            style={{ borderColor: "rgba(243,224,166,0.32)", background: "rgba(243,224,166,0.05)" }}
            data-testid="vinyl-fulfill-comp-banner"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] mb-1.5" style={{ color: "var(--mm-accent)" }}>
              A gift from George
            </p>
            <p className="text-[13px] leading-relaxed text-white/85">
              Complimentary vinyl &mdash; no charge, no shipping cost. Just drop in your postal address below and we&rsquo;ll take care of the rest.
            </p>
          </div>
        ) : (
          <div
            className="border px-4 py-3 mb-7"
            style={{ borderColor: "rgba(243,224,166,0.32)", background: "rgba(243,224,166,0.05)" }}
            data-testid="vinyl-fulfill-gift-banner"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] mb-1.5" style={{ color: "var(--mm-accent)" }}>
              Buy as a gift
            </p>
            <p className="text-[13px] leading-relaxed text-white/85">
              {Math.round((data?.discount_pct || 0.2) * 100)}% off for {data?.discount_min_qty || 2} or more purchases &mdash; applied automatically.
            </p>
          </div>
        )}

        {/* Quantity selector — hidden on comp orders (gift qty is locked) */}
        {!data?.is_comp && (
          <>
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-white/70 text-center mb-4 font-medium">
              How many records?
            </p>
            <div className="flex items-center justify-center gap-5 mb-2">
              <button
                type="button"
                onClick={() => setQty(Math.max(1, qty - 1))}
                disabled={qty <= 1}
                className="w-11 h-11 border border-white/30 flex items-center justify-center hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)] transition disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="vinyl-qty-decrement"
                aria-label="Decrease quantity"
              >
                <Minus className="w-4 h-4" strokeWidth={1.4} />
              </button>
              <div
                className="text-5xl min-w-[60px] text-center tabular-nums"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", letterSpacing: "0.02em", fontWeight: 500 }}
                data-testid="vinyl-qty-display"
              >
                {qty}
              </div>
              <button
                type="button"
                onClick={() => setQty(Math.min(data?.max_quantity || 5, qty + 1))}
                disabled={qty >= (data?.max_quantity || 5)}
                className="w-11 h-11 border border-white/30 flex items-center justify-center hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)] transition disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="vinyl-qty-increment"
                aria-label="Increase quantity"
              >
                <Plus className="w-4 h-4" strokeWidth={1.4} />
              </button>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40 text-center mb-8">
              max {data?.max_quantity || 5} per buyer
            </p>
          </>
        )}

        {/* Shipping address form */}
        <p className="font-mono text-xs uppercase tracking-[0.28em] text-white/70 mb-3 font-medium">
          Shipping address
        </p>
        <div className="flex flex-col gap-2.5 mb-6" data-testid="vinyl-shipping-form">
          <input
            type="text"
            placeholder="Full name"
            value={shipName}
            onChange={(e) => setShipName(e.target.value)}
            className={inputCls}
            data-testid="vinyl-ship-name"
            required
          />
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className={`${inputCls} appearance-none cursor-pointer`}
            data-testid="vinyl-ship-country"
            required
            style={{
              backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' stroke='white' stroke-opacity='0.5' fill='none' stroke-width='1.2'/></svg>\")",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 12px center",
            }}
          >
            <option value="" disabled>Country</option>
            {(data?.countries || []).map((c) => (
              <option key={c.code} value={c.code} style={{ background: "#0a0f1f" }}>
                {c.name}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Address line 1"
            value={line1}
            onChange={(e) => setLine1(e.target.value)}
            className={inputCls}
            data-testid="vinyl-ship-line1"
            required
          />
          <input
            type="text"
            placeholder="Address line 2 (optional)"
            value={line2}
            onChange={(e) => setLine2(e.target.value)}
            className={inputCls}
            data-testid="vinyl-ship-line2"
          />
          <div className="grid grid-cols-2 gap-2.5">
            <input
              type="text"
              placeholder="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className={inputCls}
              data-testid="vinyl-ship-city"
              required
            />
            <input
              type="text"
              placeholder="State / region"
              value={stateRegion}
              onChange={(e) => setStateRegion(e.target.value)}
              className={inputCls}
              data-testid="vinyl-ship-state"
            />
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <input
              type="text"
              placeholder="Postcode"
              value={postal}
              onChange={(e) => setPostal(e.target.value)}
              className={inputCls}
              data-testid="vinyl-ship-postcode"
              required
            />
            <input
              type="tel"
              placeholder="Phone (optional)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={inputCls}
              data-testid="vinyl-ship-phone"
            />
          </div>
        </div>

        {/* Pricing recap — shipping line only appears once a country is selected.
            Comp orders show a simple $0 total line instead of the full math. */}
        {data?.is_comp ? (
          <div className="border-t border-white/15 pt-5 mb-3" data-testid="vinyl-fulfill-pricing-comp">
            <div className="flex justify-between items-baseline">
              <span className="text-white font-mono text-[11px] uppercase tracking-[0.28em]">
                Total
              </span>
              <span
                className="tabular-nums text-[var(--mm-accent)]"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "28px", fontWeight: 500, letterSpacing: "0.01em" }}
                data-testid="vinyl-fulfill-total"
              >
                $0.00
              </span>
            </div>
            <p className="font-mono text-[10px] text-white/50 tracking-wide pt-1">
              Complimentary &mdash; shipping included &middot; estimated 2026 delivery
            </p>
          </div>
        ) : (
          <div className="border-t border-white/15 pt-5 space-y-2 mb-3" data-testid="vinyl-fulfill-pricing">
            <div className="flex justify-between items-baseline text-sm">
              <span className="text-white/60 font-mono text-[11px] uppercase tracking-[0.22em]">
                {qty} × ${Number(data?.unit_price || 60).toFixed(0)}
              </span>
              <span
                className={`tabular-nums ${pricing?.discountPct ? "text-white/40 line-through" : "text-white/85"}`}
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "18px", fontWeight: 500, letterSpacing: "0.01em" }}
              >
                ${pricing?.base?.toFixed(2)}
              </span>
            </div>
            {pricing?.discountPct ? (
              <div className="flex justify-between items-baseline text-sm" data-testid="vinyl-discount-line">
                <span className="text-[var(--mm-accent)] font-mono text-[11px] uppercase tracking-[0.22em]">
                  {Math.round(pricing.discountPct * 100)}% multi-record discount
                </span>
                <span
                  className="tabular-nums text-[var(--mm-accent)]"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "18px", fontWeight: 500, letterSpacing: "0.01em" }}
                >
                  −${pricing.discount.toFixed(2)}
                </span>
              </div>
            ) : null}
            {selectedCountry ? (
              <div className="flex justify-between items-baseline text-sm" data-testid="vinyl-shipping-line">
                <span className="text-white/60 font-mono text-[11px] uppercase tracking-[0.22em]">
                  Shipping · {selectedCountry.name}
                </span>
                <span
                  className="tabular-nums text-white/85"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "18px", fontWeight: 500, letterSpacing: "0.01em" }}
                >
                  ${pricing?.shippingAmount?.toFixed(2)}
                </span>
              </div>
            ) : (
              <p className="font-mono text-[10px] text-white/40 tracking-wide pt-1" data-testid="vinyl-shipping-placeholder">
                Shipping appears once you select a country
              </p>
            )}
            <div className="flex justify-between items-baseline pt-3 border-t border-white/10">
              <span className="text-white font-mono text-[11px] uppercase tracking-[0.28em]">
                Total
              </span>
              <span
                className="tabular-nums text-[var(--mm-accent)]"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "28px", fontWeight: 500, letterSpacing: "0.01em" }}
                data-testid="vinyl-fulfill-total"
              >
                ${selectedCountry ? pricing?.total?.toFixed(2) : pricing?.subtotal?.toFixed(2)}
              </span>
            </div>
            <p className="font-mono text-[10px] text-white/50 tracking-wide pt-1">
              Estimated 2026 delivery
            </p>
          </div>
        )}

        <button
          onClick={onCheckout}
          disabled={submitting || !addressComplete}
          className="btn-line w-full justify-center disabled:opacity-30 disabled:cursor-not-allowed text-sm py-3"
          data-testid="vinyl-fulfill-checkout"
        >
          {submitting ? (data?.is_comp ? "Confirming…" : "Securing your order…") : (<>{data?.is_comp ? "Confirm shipping address" : "Confirm your order"} <Plus className="w-4 h-4" strokeWidth={1.4} /></>)}
        </button>

        {!addressComplete && (
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40 text-center mt-3">
            Fill in your shipping address to continue
          </p>
        )}

        {error && (
          <p className="font-mono text-[11px] text-rose-300/80 text-center mt-4" data-testid="vinyl-fulfill-checkout-error">
            {error}
          </p>
        )}

        <p className="font-mono text-[9px] uppercase tracking-[0.32em] text-white/35 text-center mt-7">
          {data?.is_comp ? "Complimentary · no payment required" : "Stripe Checkout · Apple Pay / Google Pay / Card"}
        </p>
      </div>
    </main>
  );
}
